import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import * as Sentry from "@sentry/react";
import create from "zustand";
import { persist } from "zustand/middleware";
import { base64ToBlob } from "./blobUtils";
import { ensureDirectoryExists } from "./fsUtils";
import { deleteFromIndexedDB, getFromIndexedDB } from "./indexedDBStorage";

export interface OfflineTaskUpload {
  id: string;
  taskId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  addedAt: string;
  path: string;
  status: "complete" | "error" | "pending" | "uploading";
  progress: number;
  processedAt?: string;
  errorMessage?: string;
  retryCount: number;
}

interface OfflineTaskUploadsState {
  uploads: OfflineTaskUpload[];
  isProcessing: boolean;

  addUpload: (
    upload: Omit<
      OfflineTaskUpload,
      "addedAt" | "id" | "path" | "processedAt" | "progress" | "status"
    > & { id?: string }
  ) => Promise<string>;
  removeUpload: (uploadId: string) => void;
  updateUploadStatus: (
    uploadId: string,
    status: OfflineTaskUpload["status"],
    progress?: number,
    errorMessage?: string
  ) => void;
  getUploadsForTask: (taskId: string) => OfflineTaskUpload[];
  processPendingUploads: () => Promise<void>;
  getStoredFile: (uploadId: string) => Promise<File | null>;
  setProcessing: (isProcessing: boolean) => void;
  resetIncompleteUploads: () => void;
  markUploadAsProcessed: (uploadId: string) => void;
  retryFailedUploads: () => void;
  updateTaskId: (oldTaskId: string, newTaskId: string) => void;
}

const isNative = Capacitor.isNativePlatform();

export const useOfflineTaskUploadsStore = create<OfflineTaskUploadsState>(
  persist<OfflineTaskUploadsState>(
    (set, get) => ({
      addUpload: async (uploadData) => {
        const uploadId =
          uploadData.id ??
          `${uploadData.taskId}-${Date.now()}-${Math.random()}`;

        const normalizedFileName = uploadData.fileName
          .replace(/[^a-zA-Z0-9.-]/g, "_")
          .replace(/_{2,}/g, "_");

        const fileExtension = normalizedFileName.split(".").pop() ?? "";
        const safeFileName = `${uploadId}.${fileExtension}`;
        const path = `offline-task-uploads/${uploadData.taskId}/${safeFileName}`;

        const upload: OfflineTaskUpload = {
          ...uploadData,
          addedAt: new Date().toISOString(),
          id: uploadId,
          path,
          progress: 0,
          retryCount: 0,
          status: "pending",
        };

        set((state) => ({ uploads: [...state.uploads, upload] }));

        return upload.id;
      },

      getStoredFile: async (uploadId) => {
        const upload = get().uploads.find((u) => u.id === uploadId);
        if (!upload) return null;

        try {
          if (isNative) {
            await ensureDirectoryExists(upload.path);

            const { data } = await Filesystem.readFile({
              directory: Directory.Data,
              path: upload.path,
            });

            if (typeof data === "string") {
              const blob = await base64ToBlob(data, upload.fileType);
              return new File([blob], upload.fileName, {
                type: upload.fileType,
              });
            }
          } else {
            const blob = await getFromIndexedDB(upload.path);
            if (blob) {
              return new File([blob], upload.fileName, {
                type: upload.fileType,
              });
            }
          }
        } catch (error) {
          if (
            isNative &&
            error instanceof Error &&
            error.message.includes("directory")
          ) {
            try {
              await ensureDirectoryExists(upload.path);
              const { data } = await Filesystem.readFile({
                directory: Directory.Data,
                path: upload.path,
              });
              if (typeof data === "string") {
                const blob = await base64ToBlob(data, upload.fileType);
                return new File([blob], upload.fileName, {
                  type: upload.fileType,
                });
              }
            } catch (retryError) {
              Sentry.captureException(retryError, {
                extra: { context: "getStoredFile retry", uploadId },
                tags: { component: "offline-task-uploads-store" },
              });
            }
          } else {
            Sentry.captureException(error, {
              extra: { context: "getStoredFile", uploadId },
              tags: { component: "offline-task-uploads-store" },
            });
          }
        }

        return null;
      },

      getUploadsForTask: (taskId) =>
        get().uploads.filter((u) => u.taskId === taskId),

      isProcessing: false,

      markUploadAsProcessed: (uploadId) =>
        set((state) => ({
          uploads: state.uploads.map((upload) =>
            upload.id === uploadId
              ? { ...upload, processedAt: new Date().toISOString() }
              : upload
          ),
        })),

      processPendingUploads: async () => {
        const { uploads } = get();
        const pendingUploads = uploads.filter((u) => u.status === "pending");

        if (pendingUploads.length === 0) return;

        const firstPending = pendingUploads[0];
        get().updateUploadStatus(firstPending.id, "uploading", 0);
      },

      removeUpload: (uploadId) => {
        const upload = get().uploads.find((u) => u.id === uploadId);
        if (upload) {
          if (isNative) {
            Filesystem.deleteFile({
              directory: Directory.Data,
              path: upload.path,
            }).catch((error) => {
              Sentry.captureException(error, {
                extra: { context: "removeUpload filesystem", uploadId },
                tags: { component: "offline-task-uploads-store" },
              });
            });
          } else {
            deleteFromIndexedDB(upload.path).catch((error) => {
              Sentry.captureException(error, {
                extra: { context: "removeUpload indexedDB", uploadId },
                tags: { component: "offline-task-uploads-store" },
              });
            });
          }
        }

        set((state) => ({
          uploads: state.uploads.filter((u) => u.id !== uploadId),
        }));
      },

      resetIncompleteUploads: () =>
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.status === "complete" && !u.processedAt
              ? { ...u, progress: 0, status: "pending" }
              : u
          ),
        })),

      retryFailedUploads: () =>
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.status === "error"
              ? { ...u, retryCount: u.retryCount + 1, status: "pending" }
              : u
          ),
        })),

      setProcessing: (isProcessing) => set({ isProcessing }),

      updateTaskId: (oldTaskId, newTaskId) => {
        const currentUploads = get().uploads;
        const uploadsToUpdate = currentUploads.filter(
          (u) => u.taskId === oldTaskId
        );

        if (uploadsToUpdate.length === 0) return;

        set((state) => ({
          uploads: state.uploads.map((u) => {
            if (u.taskId !== oldTaskId) return u;

            const newPath = u.path.replace(
              `/offline-task-uploads/${oldTaskId}/`,
              `/offline-task-uploads/${newTaskId}/`
            );

            const shouldResetStatus =
              u.status === "pending" || u.status === "error";
            const newStatus = shouldResetStatus ? "pending" : u.status;

            return {
              ...u,
              path: newPath,
              status: newStatus,
              taskId: newTaskId,
            };
          }),
        }));

        setTimeout(() => {
          void get().processPendingUploads();
        }, 0);
      },

      updateUploadStatus: (uploadId, status, progress, errorMessage) =>
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.id === uploadId
              ? {
                  ...u,
                  errorMessage,
                  progress: progress ?? u.progress,
                  status,
                }
              : u
          ),
        })),

      uploads: [],
    }),
    {
      name: "offline-task-uploads",
      version: 1,
    }
  )
);
